/**
 * Scout › Búsqueda › detalle — candidatos de una búsqueda. Cada fila linkea a
 * la página de detalle del candidato (scoring completo + evidencia). Ver
 * docs/scout-plan/04.
 */
import { type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchScoutSearchCandidates } from '../../api/admin/scout.js';
import { fmt, recLabel, recPill, scoresTitle, statusPill } from './format.js';

export function ScoutDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['admin', 'scout', 'candidates', id],
    queryFn: () => fetchScoutSearchCandidates(id!),
    enabled: !!id,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Scout · {query.data?.search.title ?? 'Búsqueda'}
          </h1>
          <p className="page-sub">
            <Link to="/scout">← Volver a búsquedas</Link>
          </p>
        </div>
      </div>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Candidato</th>
                <th>Email</th>
                <th>Estado</th>
                <th>Recomendación</th>
                <th>Resumen</th>
                <th>CV</th>
                <th>Registrado</th>
              </tr>
            </thead>
            <tbody>
              {query.data.candidates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty">
                    Todavía no se registró ningún candidato con este link.
                  </td>
                </tr>
              ) : (
                query.data.candidates.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/scout/candidate/${c.id}`}>{c.fullName}</Link>
                    </td>
                    <td>
                      <a href={`mailto:${c.email}`}>{c.email}</a>
                    </td>
                    <td>
                      <span className={statusPill(c.status)}>{c.status}</span>
                    </td>
                    <td>
                      {c.result ? (
                        <span className={recPill(c.result.recommendation)} title={scoresTitle(c.result)}>
                          {recLabel(c.result.recommendation)}
                          {c.result.confidence === 'low' ? ' · baja conf.' : ''}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      {c.result?.summary ? (
                        <span title={c.result.summary}>{c.result.summary}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {c.hasCv ? (
                        // Descarga directa (cookie admin same-origin). Siempre attachment.
                        <a href={`/api/admin/scout/candidates/${c.id}/cv`}>Descargar</a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{fmt(c.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
